import { reviewWorkflowStepLabel } from "@/components/case-checking/CaseReviewWorkflow";
import type {
  CaseReviewOnRerunMetadata,
  ClearedPriorReview,
  GuidedReviewStep,
  ProblemStatementRun,
  PsRuleHumanReviewRun,
} from "@/services/api/case-checking";

export type ReviewProgressSummary = {
  hasProgress: boolean;
  currentStepLabel: string;
  ruleOverrides: number;
  disputes: number;
  hasCompletenessDecision: boolean;
  psSignoffs: number;
  remedialEdits: number;
  completedSteps: number;
};

function stepLabel(step: GuidedReviewStep | undefined): string {
  return step ? reviewWorkflowStepLabel(step) : "the review";
}

function countPsSignoffs(psRun: ProblemStatementRun | null | undefined): number {
  if (!psRun?.human_reviews?.length) return 0;
  return psRun.human_reviews.filter((r) => Boolean(r.reviewed_at)).length;
}

export function summarizeReviewProgress(
  run: PsRuleHumanReviewRun | null | undefined,
  psRun?: ProblemStatementRun | null,
): ReviewProgressSummary {
  if (!run) {
    return {
      hasProgress: false,
      currentStepLabel: stepLabel("narrative"),
      ruleOverrides: 0,
      disputes: 0,
      hasCompletenessDecision: false,
      psSignoffs: 0,
      remedialEdits: 0,
      completedSteps: 0,
    };
  }

  const ruleOverrides = run.reviews?.length ?? 0;
  const disputes = Object.keys(run.checklist_disputes ?? {}).length;
  const hasCompletenessDecision = run.completeness_review != null;
  const remedialEdits = run.remedial_edits?.length ?? 0;
  const psSignoffs = countPsSignoffs(psRun);
  const completedSteps = run.completed_steps?.length ?? 0;
  const hasProgress =
    ruleOverrides > 0 ||
    disputes > 0 ||
    hasCompletenessDecision ||
    remedialEdits > 0 ||
    run.current_step !== "narrative";

  return {
    hasProgress,
    currentStepLabel: stepLabel(run.current_step),
    ruleOverrides,
    disputes,
    hasCompletenessDecision,
    psSignoffs,
    remedialEdits,
    completedSteps,
  };
}

function joinList(parts: string[]): string {
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

export function formatClearedProgressPhrase(cleared: ClearedPriorReview): string {
  const parts: string[] = [];
  if (cleared.rule_overrides > 0) {
    parts.push(
      `${cleared.rule_overrides} rule override${cleared.rule_overrides === 1 ? "" : "s"}`,
    );
  }
  if (cleared.disputes > 0) {
    parts.push(`${cleared.disputes} document note${cleared.disputes === 1 ? "" : "s"}`);
  }
  if (cleared.had_completeness_decision) {
    parts.push("your completeness decision");
  }
  if (cleared.ps_signoffs > 0) {
    parts.push(`${cleared.ps_signoffs} sign-off${cleared.ps_signoffs === 1 ? "" : "s"}`);
  }
  if (cleared.remedial_edits > 0) {
    parts.push(
      `${cleared.remedial_edits} remedial edit${cleared.remedial_edits === 1 ? "" : "s"}`,
    );
  }
  return joinList(parts);
}

const RERUN_SOURCE_LABELS: Record<string, string> = {
  mixed_addition: "new documents were added",
  uploaded: "new documents were uploaded",
  progeny_pull: "documents were pulled from Progeny",
  progeny: "documents were updated from Progeny",
  review_folder: "documents were added from the review folder",
  review_supplements: "supplement documents were added",
  scheduled_document_queue: "the scheduled overnight run",
  scheduled: "the scheduled overnight run",
};

export function clearedRerunSourceLabel(source: string | undefined | null): string {
  const key = (source ?? "").trim();
  if (!key) return "the case check re-ran";
  return RERUN_SOURCE_LABELS[key] ?? "the case check re-ran";
}

export function formatReviewProgressLossPhrase(progress: ReviewProgressSummary): string {
  const parts: string[] = [];
  if (progress.ruleOverrides > 0) {
    parts.push(
      `${progress.ruleOverrides} rule change${progress.ruleOverrides === 1 ? "" : "s"}`,
    );
  }
  if (progress.disputes > 0) {
    parts.push(`${progress.disputes} document note${progress.disputes === 1 ? "" : "s"}`);
  }
  if (progress.hasCompletenessDecision) {
    parts.push("a completeness decision");
  }
  if (progress.psSignoffs > 0) {
    parts.push(`${progress.psSignoffs} sign-off${progress.psSignoffs === 1 ? "" : "s"}`);
  }
  if (progress.remedialEdits > 0) {
    parts.push(
      `${progress.remedialEdits} remedial edit${progress.remedialEdits === 1 ? "" : "s"}`,
    );
  }
  return joinList(parts);
}

export function parseCaseReviewOnRerun(
  metadata: { review_on_rerun?: unknown } | null | undefined,
): CaseReviewOnRerunMetadata | null {
  const raw = metadata?.review_on_rerun;
  if (!raw || typeof raw !== "object") return null;
  return raw as CaseReviewOnRerunMetadata;
}
